import React, {useState} from 'react';

import classNames from 'classnames';

import {AutoHeightTitleInput} from 'app/components/general/AutoHeightTitleInput';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {CollapsableFolderPickerForm} from 'app/folders/components/CollapsableFolderPickerForm';
import {FormControlGroup, ShowNonFieldFormErrors} from 'app/general/forms';
import {DocHeaderStructure} from 'app/general/structures/DocHeader';
import {TitleSuggestions} from 'app/openai/components/TitleSuggestions';
import {CollapsableTeamPickerForm} from 'app/teams/components/CollapsableTeamPickerForm';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {capitalizeFirstLetter} from 'app/utils/strings';

import type {ReactNode} from 'react';
import type {
  ContentType,
  FolderSerializedType,
  TeamSerializedType,
} from 'sweet-types';

type Props = {
  onCreate: (name: string, generateContent: boolean) => Promise<unknown>;
  forceDisableConfirm: boolean;
  disabledTooltip?: string;
  ct: ContentType;
  defaultName?: string;
  initialFolder: null | string;
  disableFolder?: boolean;
  teams: Array<TeamSerializedType>;
  disabledTeamText?: (team: TeamSerializedType) => string | undefined;
  onChangeTeams: (teams: Array<TeamSerializedType>) => Promise<unknown>;
  onChangeFolders: (folders: Array<FolderSerializedType>) => Promise<unknown>;
  advancedContent?: ReactNode;
};

export function NewDocumentMetadata({
  onCreate,
  forceDisableConfirm = false,
  disabledTooltip,
  disabledTeamText,
  ct,
  defaultName,
  initialFolder = null,
  disableFolder = false,
  teams,
  onChangeTeams,
  onChangeFolders,
  advancedContent,
}: Props) {
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [title, setTitle] = useState<string | undefined>(defaultName);
  const [isGenerateContent, setIsGenerateContent] = useState<boolean>(false);
  const contentType = getContentType(ct);
  const docType = capitalizeFirstLetter(contentType.name);
  const pluralContentName = {procedure: 'procedures', policy: 'policies'}[ct];

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isCreating || forceDisableConfirm || title == null) {
      return;
    }

    setIsCreating(true);

    const disableCreating = () => setIsCreating(false);

    onCreate(title, isGenerateContent)
      .catch(disableCreating)
      .then(disableCreating);
  };

  return (
    <form onSubmit={handleSubmit}>
      <DocHeaderStructure
        titleContent={
          <h1>
            <FormControlGroup field="name">
              <AutoHeightTitleInput
                value={title}
                className="form-control"
                onChange={setTitle}
                autoFocus={true}
                type="text"
                maxLength={128}
                name="title"
                placeholder={gt('%(docType)s Title', {
                  docType,
                })}
              />
            </FormControlGroup>
          </h1>
        }
        extraContent={
          <>
            <div className="d-flex mb-1">
              <div className="flex-grow-1 mr-2">
                <CollapsableTeamPickerForm
                  selected={teams}
                  onChange={onChangeTeams}
                  disabledText={disabledTeamText}
                />
              </div>
              {ct === 'procedure' || ct === 'policy' ? (
                <SweetAIButton
                  isTitleEmpty={title === '' || title == null}
                  isCreating={isCreating}
                  forceDisableConfirm={forceDisableConfirm}
                  disabledTooltip={disabledTooltip}
                  setIsGenerateContent={setIsGenerateContent}
                />
              ) : null}
              <ContinueButton
                isTitleEmpty={title === '' || title == null}
                isCreating={isCreating}
                forceDisableConfirm={forceDisableConfirm}
                disabledTooltip={disabledTooltip}
              />
            </div>
            <ShowNonFieldFormErrors />
            {!disableFolder && (
              <CollapsableFolderPickerForm
                initialSelectedFolder={initialFolder}
                onChange={onChangeFolders}
              />
            )}
            {ct == 'procedure' || ct === 'policy' ? (
              <TitleSuggestions
                contentType={ct}
                contentTypePlural={pluralContentName}
                onClick={setTitle}
              />
            ) : null}
          </>
        }
        advancedContent={advancedContent}
      />
    </form>
  );
}

type ContinueProps = {
  isCreating: boolean;
  forceDisableConfirm: boolean;
  disabledTooltip?: string;
  isTitleEmpty: boolean;
};

function ContinueButton({
  isCreating,
  forceDisableConfirm,
  disabledTooltip,
  isTitleEmpty,
}: ContinueProps) {
  const confirmClasses = classNames('btn', 'btn-primary', {
    disabled: isCreating || forceDisableConfirm || isTitleEmpty,
  });

  return (
    <LazyTooltip hide={!forceDisableConfirm} tooltip={disabledTooltip}>
      <button type="submit" className={confirmClasses}>
        {isCreating ? gt('Creating') : gt('Continue')}
      </button>
    </LazyTooltip>
  );
}

type SweetAIButtonProps = {
  isCreating: boolean;
  forceDisableConfirm: boolean;
  disabledTooltip?: string;
  setIsGenerateContent: (checked: boolean) => unknown;
  isTitleEmpty: boolean;
};

function SweetAIButton({
  isCreating,
  forceDisableConfirm,
  disabledTooltip,
  setIsGenerateContent,
  isTitleEmpty,
}: SweetAIButtonProps) {
  const confirmClasses = classNames('btn', 'btn-outline-primary', 'mr-2', {
    disabled: isCreating || forceDisableConfirm || isTitleEmpty,
  });

  const tooltip = forceDisableConfirm
    ? disabledTooltip
    : gt(
        'Use our Sweet Artificial Intelligence (SweetAI) to write this document based on the title'
      );

  return (
    <LazyTooltip tooltip={tooltip}>
      <button
        type="submit"
        className={confirmClasses}
        onClick={() => setIsGenerateContent(true)}
      >
        {isCreating ? gt('Creating') : gt('Write with SweetAI')}
      </button>
    </LazyTooltip>
  );
}
